import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TableService } from '../../../services/table.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { FNFResponse } from '../../../models/response';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { RestaurantService } from '../../../services/restaurant.service';
import jsPDF from 'jspdf';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { SettingService } from '../../../services/setting.service';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../../utils/feature-flag.util';
import { FloorPlanComponent } from './floor-plan/floor-plan.component';
import { TableCombinationsComponent } from './combinations/table-combinations.component';

export type TableStatusFilter = 'all' | 'available' | 'occupied';
export type TableView = 'list' | 'grid' | 'layout' | 'combinations';
export type TableGroupBy = 'zone' | 'none';

export interface TableGroup {
  /** Null for the single bucket used when grouping is off. */
  zoneId: string | null;
  label: string;
  tables: any[];
}

@Component({
  selector: 'app-tables',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTooltipModule,
    FormsModule,
    FloorPlanComponent,
    TableCombinationsComponent,
    RouterLink,
  ],
  templateUrl: './tables.component.html',
  styleUrl: './tables.component.css',
})
export class TablesComponent {
  tableService = inject(TableService);
  restaurantService = inject(RestaurantService);
  settingService = inject(SettingService);
  /**
   * Why guests cannot start a table here, or null when they can (FNF-1223):
   * Dine-In switched off in Restaurant → Settings, or not covered by the plan.
   * Null too for a POS that has no Dine-In at all (Square).
   */
  dineInOff: { requiredPlan: string | null } | null = null;
  /** The Settings page exists only behind the RESTAURANT_SETTINGS flag. */
  canOpenSettings = false;
  private subscriptions = new Subscription();
  ngxSpinnerService = inject(NgxSpinnerService);
  tables: any = []; // List of tables fetched from the API
  unFilteredTables: any = [];
  zones: any = [];
  displayLoading: boolean = false;
  /**
   * The tables request failed with nothing loaded to show. Rendered as an
   * error with "Try again" instead of "No Tables", so a failed load is never
   * mistaken for a restaurant without tables.
   */
  tablesLoadFailed = false;
  /**
   * 'list' renders the grouped tables, 'grid' the card layout, and 'layout'
   * the per-zone floor-plan editor (which ignores status/group filters).
   */
  view: TableView = 'list';
  statusFilter: TableStatusFilter = 'all';
  groupBy: TableGroupBy = 'zone';
  /** Tables after search + status filter, bucketed for rendering. */
  groups: TableGroup[] = [];
  selectedQrCode: any = null;
  @ViewChild('qrCodeImage') qrCodeImage!: ElementRef;
  router = inject(Router);
  /** View, status filter and group by live in the URL (?view, ?status,
   * ?group), so a browser reload reopens the same view. ?view is also how
   * Reservations links straight to the floor plan or the combinations. */
  private readonly urlState = injectUrlViewState();
  searchQuery: string = '';

  readonly statusFilters: { value: TableStatusFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'available', label: 'Available' },
    { value: 'occupied', label: 'Occupied' },
  ];

  /**
   * Grouping by zone already names the zone in each section header, and
   * filtering to a single status makes that column one repeated value, so
   * both drop out rather than repeating themselves down the rows.
   */
  get showZoneColumn(): boolean {
    return this.groupBy !== 'zone';
  }

  get showStatusColumn(): boolean {
    return this.statusFilter === 'all';
  }

  get hasResults(): boolean {
    return this.groups.some((group) => group.tables.length > 0);
  }

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    this.view = this.urlState.pick<TableView>(
      'view',
      ['list', 'grid', 'layout', 'combinations'],
      'list',
    );
    this.statusFilter = this.urlState.pick<TableStatusFilter>(
      'status',
      ['all', 'available', 'occupied'],
      'all',
    );
    this.groupBy = this.urlState.pick<TableGroupBy>('group', ['zone', 'none'], 'zone');
    this.getTables(); // Fetch the list of tables on component initialization
    this.getZones();
    this.watchDineIn();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /** Follows the restaurant's Dine-In switch and the Settings flag. The
   * settings are loaded for the whole app (sidebar) and kept in step by the
   * Settings page, so a toggle there shows here at once. */
  private watchDineIn() {
    this.subscriptions.add(
      this.settingService.currentRestaurantSettings$.subscribe((rows: any) => {
        this.dineInOff = dineInOffFrom(rows);
      }),
    );
    this.subscriptions.add(
      this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
        this.canOpenSettings = isFeatureActive(flags, FEATURE_FLAG_KEYS.RESTAURANT_SETTINGS);
      }),
    );
    if (this.settingService.currentRestaurantSettings$.value == null) {
      this.settingService.getSettings().subscribe((response: any) => {
        if (response?.data) this.settingService.currentRestaurantSettings$.next(response.data);
      });
    }
  }

  /**
   * `showLoading=false` refetches in place: the floor-plan editor keeps its
   * unsaved draft only while it stays mounted, and the loading state would
   * unmount it.
   */
  getZones(showLoading = true) {
    if (showLoading) {
      this.displayLoading = true;
    }
    this.restaurantService.getRestaurantZones().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.zones = response.data;
        // Zones and tables load independently; regroup so section order
        // follows the zone list however the two responses interleave.
        this.filterTables();
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  createTableConfirm() {
    if (this.zones.length == 0) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: 'No zones found. Please add a zone first to create a table.',
          buttonText: {
            ok: 'Create zone now',
            cancel: 'Add later',
          },
          title: 'No zones found',
        },
      });
      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          // returnTo brings the user back to Tables once the zone is saved.
          this.router.navigate(['/add-zone'], { queryParams: { returnTo: this.router.url } });
        }
      });
    } else {
      this.addNewTable();
    }
  }

  // Opens a modal to add or edit a table
  addNewTable(table?: any) {
    const id = table ? (table.table_id ?? table.id) : null;
    this.router.navigate(id ? ['/edit-table', id] : ['/add-table']);
  }

  // Opens a confirmation dialog to delete a table
  deleteTableConfirm(table: any) {
    if (table.status == 'occupied') {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message:
            'The <span class="text-black" >' +
            table.table_name +
            '</span> table has running orders. Please try to delete later.',
          buttonText: {
            ok: 'Okay',
          },
          title: "Sorry, you can't delete",
        },
      });
    } else if (table.status == 'reserved') {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message:
            'The <span class="text-black" >' +
            table.table_name +
            '</span> table already reserved. Please try to delete later.',
          buttonText: {
            ok: 'Okay',
          },
          title: "Sorry, you can't delete",
        },
      });
    } else {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message:
            'This will permanently delete <span class="text-black" >' +
            table.table_name +
            '</span> Table and you can’t undo this later.',
          buttonText: {
            ok: 'Yes, Delete',
            cancel: 'No, Cancel',
          },
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteTable(table);
        }
      });
    }
  }

  // Deletes a table from the database
  deleteTable(table: any) {
    this.ngxSpinnerService.show();
    this.tableService.deleteTable(table.table_id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.getTables();
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.getTables();
          this.ngxSpinnerService.hide();
          return;
        }
        this.toastr.success('Table deleted successfully', 'Success');
        this.ngxSpinnerService.hide();
        this.getTables();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
        this.getTables();
      },
    });
  }

  // Toggles between the list, grid and floor-plan views
  changeView(view: TableView) {
    this.view = view;
    this.urlState.set('view', view, 'list');
  }

  /** The floor plan saved (or hit a conflict): refresh without unmounting it. */
  onLayoutChanged() {
    this.getTables(false);
    this.getZones(false);
  }

  setStatusFilter(status: TableStatusFilter) {
    this.statusFilter = status;
    this.urlState.set('status', status, 'all');
    this.filterTables();
  }

  setGroupBy(groupBy: TableGroupBy) {
    this.groupBy = groupBy;
    this.urlState.set('group', groupBy, 'zone');
    this.filterTables();
  }

  /** Boxicons glyph per table shape, mirroring the icons in the design. */
  shapeIcon(table: any): string {
    switch ((table?.shape || '').toLowerCase()) {
      case 'square':
        return 'bx-square';
      case 'rectangle':
        return 'bx-rectangle';
      case 'circle':
        return 'bx-circle';
      case 'oval':
        return 'bx-shape-circle';
      default:
        return 'bx-shape-square';
    }
  }

  /** Shape name shown beside its glyph; unset shapes read as a dash. */
  shapeLabel(table: any): string {
    return table?.shape ? this.toTitleCase(table.shape) : '—';
  }

  waiterLabel(table: any): string {
    const waiter = table?.current_server;
    if (!waiter) {
      return '—';
    }
    const last = waiter.last_name ? `${String(waiter.last_name).charAt(0)}.` : '';
    return [waiter.first_name, last].filter(Boolean).join(' ').trim() || '—';
  }

  viewQrCode(table: any) {
    this.selectedQrCode = table;
  }

  retryTables() {
    this.getTables();
  }

  // Fetches all tables from the API
  private getTables(showLoading = true) {
    if (showLoading) {
      this.displayLoading = true;
    }
    this.tableService.getAllTables().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          // A network failure (timeout, blocked, gateway drop) carries no
          // server message, so the toast says what failed instead of nothing.
          this.toastr.error(
            response.error.error?.message ||
              response.error.message ||
              'Unable to load tables. Please try again.',
            'Error',
          );
          this.onTablesLoadFailed();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.onTablesLoadFailed();
          return;
        }
        this.unFilteredTables = response.data;
        this.tablesLoadFailed = false;
        this.filterTables();
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.onTablesLoadFailed();
      },
    });
  }

  // A quiet refresh that fails keeps the tables already on screen; only a
  // load with nothing to show falls back to the error state.
  private onTablesLoadFailed() {
    this.tablesLoadFailed = !this.unFilteredTables?.length;
    this.displayLoading = false;
  }

  printQRCode(table: any): void {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
            <html>
                <head>
                <style>
                    /* Center the image and text on the page */
                    body {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100vh;
                    margin: 0;
                    font-family: Arial, sans-serif;
                    }
                    img {
                    max-width: 100%;
                    max-height: 100%;
                    border: 1px solid #ccc;
                    padding: 10px;
                    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
                    }
                    p {
                    margin-top: 20px;
                    font-size: 36px;
                    font-weight: bold;
                    color: #333;
                    }
                </style>
                </head>
                <body>
                <img src="${table.qr_code_path}" alt="QR Code" />
                <p>${table.table_name}</p>
                </body>
            </html>
            `);

      // printWindow.document.close();

      // Automatically close the print window after printing
      printWindow.onafterprint = () => {
        printWindow.close();
      };

      // Trigger the print dialog
      printWindow.print();
    }
  }

  downloadAsPDF(table: any): void {
    const doc = new jsPDF();
    // Add image to PDF
    doc.addImage(table.qr_code_path, 'PNG', 10, 10, 100, 100);
    doc.save(table.table_name + '.pdf');
  }

  downloadImage(table: any) {
    const imgElement: HTMLImageElement = this.qrCodeImage.nativeElement;
    // const imageUrl = imgElement.src;

    // Fetch the image data
    fetch(table.qr_code_path, {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache' },
    })
      .then((response) => response.blob())
      .then((blob) => {
        // Create a URL for the Blob and download it
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = table.table_name + '.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => {
        console.error('Error downloading the image:', error);
      });
  }

  markAsAvailable(table: any) {
    if (table.status != 'available') {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message:
            'Do you want to mark <span class="text-black" >' +
            table.table_name +
            '</span> as available? The table status will change from <span class="text-black" >' +
            table.status +
            '</span> to <span class="text-black" >Available</span>.',
          buttonText: {
            ok: 'Yes, Confirm',
            cancel: 'No, Cancel',
          },
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.markAsAvailableTable(table);
        }
      });
    }
  }

  markAsAvailableTable(table: any) {
    this.ngxSpinnerService.show();
    this.tableService.editTablePatch({ table_id: table.table_id, status: 'available' }).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.toastr.success('Table updated successfully', 'Success');
        this.ngxSpinnerService.hide();
        const updated = this.tables.find((t: any) => t.table_id == table.table_id);
        if (updated) {
          updated.status = 'available';
        }
        const unfiltered = this.unFilteredTables.find((t: any) => t.table_id == table.table_id);
        if (unfiltered) {
          unfiltered.status = 'available';
        }
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  filterTables() {
    const query = this.searchQuery.trim().toLowerCase();

    let result = [...this.unFilteredTables];
    if (this.statusFilter !== 'all') {
      result = result.filter((table: any) => table.status === this.statusFilter);
    }
    if (query) {
      result = result.filter((table: any) => this.getTableSearchText(table).includes(query));
    }

    this.tables = result;
    this.groups = this.buildGroups(result);
  }

  /**
   * Buckets the visible tables for rendering. Grouping off still yields one
   * group so the template has a single shape to iterate.
   */
  private buildGroups(tables: any[]): TableGroup[] {
    if (this.groupBy !== 'zone') {
      return [{ zoneId: null, label: 'All Tables', tables }];
    }

    const buckets = new Map<string, TableGroup>();
    // Seed from the zone list so section order follows the restaurant's own
    // ordering rather than whichever table happens to come back first.
    for (const zone of this.zones || []) {
      const id = String(zone.zone_id ?? zone.id ?? zone.zone_name);
      buckets.set(id, { zoneId: id, label: zone.zone_name, tables: [] });
    }

    const unzoned: any[] = [];
    for (const table of tables) {
      const zone = table.zone;
      const id = zone ? String(zone.zone_id ?? zone.id ?? zone.zone_name) : null;
      if (id && buckets.has(id)) {
        buckets.get(id)!.tables.push(table);
      } else if (id) {
        buckets.set(id, { zoneId: id, label: zone.zone_name, tables: [table] });
      } else {
        unzoned.push(table);
      }
    }

    const groups = [...buckets.values()].filter((group) => group.tables.length > 0);
    if (unzoned.length) {
      groups.push({ zoneId: null, label: 'Unassigned', tables: unzoned });
    }
    return groups;
  }

  editZone(group: TableGroup) {
    this.router.navigate(['/main/zone']);
  }

  private getTableSearchText(table: any): string {
    const waiter = table.current_server;
    const waiterName = waiter
      ? [waiter.first_name, waiter.last_name].filter(Boolean).join(' ').trim()
      : '';

    const values = [
      table.table_name,
      table.status,
      table.status ? this.toTitleCase(table.status) : '',
      table.shape,
      table.shape ? this.toTitleCase(table.shape) : '',
      table.number_of_seats?.toString(),
      table.zone?.zone_name,
      waiterName,
      waiter?.first_name,
      waiter?.last_name,
    ];

    return values
      .filter((value) => value != null && value !== '')
      .map((value) => String(value).toLowerCase())
      .join(' ');
  }

  private toTitleCase(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
}

/**
 * Dine-In as the restaurant's settings rows say (FNF-1223): off when the
 * switch is off or the plan does not cover it; null when it is on, when the
 * POS has no Dine-In (availability.visible false) or the rows have not loaded.
 */
export function dineInOffFrom(rows: any): { requiredPlan: string | null } | null {
  if (!Array.isArray(rows)) return null;
  const order = rows.find((row: any) => row?.setting_type === 'order');
  if (!order?.settings_config) return null;
  const gate = order.availability?.dine_in;
  if (gate?.visible === false) return null;
  if (gate && gate.eligible === false) return { requiredPlan: gate.required_plan || null };
  return order.settings_config.dine_in?.enabled === true ? null : { requiredPlan: null };
}
